import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import TopoCanvas from "./TopoCanvas";
import useIntroSound from "./useIntroSound";

const TICKER = [
  "SIRGAS 2000",
  "FUSO UTM 24S",
  "RTK · DRONES · ESTAÇÕES TOTAIS",
  "FORTALEZA — CE",
];

const BRAND = "LEVANTAMENTOS TOPOGRÁFICOS";

export default function Intro({ onComplete }) {
  const [show, setShow] = useState(true);
  const [pct, setPct] = useState(0);
  const [tick, setTick] = useState(0);
  const cb = useRef(onComplete);
  cb.current = onComplete;
  const { enabled, toggle, start, fadeOut } = useIntroSound();
  const introActive = useRef(true);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const finish = () => {
      introActive.current = false;
      fadeOut();
      setPct(100);
      cb.current?.();
      setShow(false);
      document.body.style.overflow = "";
    };
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      finish();
      return;
    }
    const p = setInterval(() => setPct((v) => Math.min(100, v + 6 + Math.random() * 14)), 150);
    const t = setInterval(() => setTick((i) => (i + 1) % TICKER.length), 520);
    const timer = setTimeout(finish, 2400);
    return () => {
      clearInterval(p);
      clearInterval(t);
      clearTimeout(timer);
      document.body.style.overflow = "";
    };
  }, [fadeOut]);

  useEffect(() => {
    const kick = () => {
      if (introActive.current) start();
    };
    let cancelled = false;
    const result = start();
    if (result && typeof result.then === "function") {
      result.then((running) => {
        if (!running && !cancelled && introActive.current) {
          window.addEventListener("pointerdown", kick, { once: true });
          window.addEventListener("keydown", kick, { once: true });
        }
      });
    }
    return () => {
      cancelled = true;
      window.removeEventListener("pointerdown", kick);
      window.removeEventListener("keydown", kick);
    };
  }, [start]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="intro-overlay"
          initial={{ clipPath: "inset(0 0 0% 0)" }}
          exit={{ clipPath: "inset(0 0 100% 0)" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-ink overflow-hidden"
          aria-hidden="true"
        >
          <TopoCanvas className="absolute inset-0 w-full h-full opacity-50" />

          <button
            data-testid="intro-sound-toggle"
            onClick={toggle}
            aria-label={enabled ? "Desativar som" : "Ativar som"}
            className="absolute top-6 right-6 z-10 flex h-10 w-10 items-center justify-center rounded-full glass text-slate-300 transition-colors hover:text-white"
          >
            {enabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>

          <motion.div
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
            className="relative flex flex-col items-center"
          >
            <svg viewBox="0 0 200 200" className="h-44 w-44 sm:h-52 sm:w-52" fill="none">
              <defs>
                <linearGradient id="introGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#0062ff" />
                  <stop offset="0.55" stopColor="#38bdf8" />
                  <stop offset="1" stopColor="#00f0ff" />
                </linearGradient>
              </defs>
              <g className="radar-spin">
                <circle
                  cx="100"
                  cy="100"
                  r="80"
                  stroke="rgba(0,240,255,0.25)"
                  strokeWidth="1"
                  strokeDasharray="3 7"
                />
                <line x1="100" y1="100" x2="100" y2="20" stroke="rgba(0,240,255,0.45)" strokeWidth="1" />
              </g>
              <line x1="100" y1="14" x2="100" y2="26" stroke="rgba(0,240,255,0.4)" strokeWidth="1" />
              <line x1="100" y1="174" x2="100" y2="186" stroke="rgba(0,240,255,0.4)" strokeWidth="1" />
              <line x1="14" y1="100" x2="26" y2="100" stroke="rgba(0,240,255,0.4)" strokeWidth="1" />
              <line x1="174" y1="100" x2="186" y2="100" stroke="rgba(0,240,255,0.4)" strokeWidth="1" />
              <motion.path
                d="M38 130 C 70 112, 132 134, 166 86"
                stroke="url(#introGrad)"
                strokeWidth="5"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ delay: 0.25, duration: 1.1, ease: "easeInOut" }}
              />
              <motion.text
                x="100"
                y="114"
                textAnchor="middle"
                fontFamily="Outfit, sans-serif"
                fontWeight="900"
                fontSize="58"
                fill="#f8fafc"
                letterSpacing="-3"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              >
                DM
              </motion.text>
            </svg>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.028, delayChildren: 0.85 } } }}
              className="mt-5 flex"
            >
              {BRAND.split("").map((ch, i) => (
                <motion.span
                  key={i}
                  variants={{
                    hidden: { opacity: 0, y: 10 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
                  }}
                  className="font-mono text-[11px] sm:text-xs tracking-[0.45em] text-slate-300"
                >
                  {ch === " " ? "\u00A0" : ch}
                </motion.span>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="absolute bottom-14 inset-x-0 flex flex-col items-center gap-4 px-8"
          >
            <div className="h-px w-56 sm:w-72 overflow-hidden rounded-full bg-white/10">
              <div
                data-testid="intro-progress"
                className="h-full bg-gradient-to-r from-dm-blue via-dm-glow to-dm-contour transition-all duration-150"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex w-56 sm:w-72 items-center justify-between font-mono text-[9px] tracking-[0.25em] text-slate-500">
              <span className="max-w-[70%] truncate whitespace-nowrap">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={tick}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.22 }}
                    className="block"
                  >
                    {TICKER[tick]}
                  </motion.span>
                </AnimatePresence>
              </span>
              <span className="text-dm-glow">{Math.round(pct)}%</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
